import { IPlayerSeasonStats } from "../../types/types";
import { normalizeRole, type PlayerRole } from "../leaderboard/roles";

export type SeasonStatsSortKey =
  | "playerName"
  | "teamId"
  | "role"
  | "kda"
  | "kills"
  | "deaths"
  | "assists"
  | "csPerMin"
  | "kpPct"
  | "dpm"
  | "doubleKills"
  | "tripleKills"
  | "quadraKills"
  | "pentaKills"
  | "gamesPlayed"
  | "champsPlayed";

export type SortDirection = "asc" | "desc";

const ASC_FIRST_SORT_KEYS = new Set<SeasonStatsSortKey>(["playerName", "teamId", "role"]);

const MULTIKILL_SORT_KEYS = new Set<SeasonStatsSortKey>([
  "doubleKills",
  "tripleKills",
  "quadraKills",
  "pentaKills",
]);

const MULTIKILL_TIER_ORDER: SeasonStatsSortKey[] = [
  "pentaKills",
  "quadraKills",
  "tripleKills",
  "doubleKills",
];

export function defaultSeasonStatsSortDirection(key: SeasonStatsSortKey): SortDirection {
  return ASC_FIRST_SORT_KEYS.has(key) ? "asc" : "desc";
}

export function isMultikillSortKey(key: SeasonStatsSortKey): boolean {
  return MULTIKILL_SORT_KEYS.has(key);
}

export type SeasonStatsRosterFilter = "all" | "main" | "substitute" | "benched";

export interface SeasonStatsFilters {
  teamId?: string | null;
  role?: PlayerRole | null;
  roster?: SeasonStatsRosterFilter | null;
}

export function normalizeSeasonStatsRole(value: string | undefined): PlayerRole | null {
  return normalizeRole(value);
}

function hasStatValue(value: number | undefined): boolean {
  return value != null && !Number.isNaN(value) && value !== 0;
}

export function hasSeasonStats(row: IPlayerSeasonStats): boolean {
  if ((row.gamesPlayed ?? 0) > 0) return true;
  if ((row.champsPlayed ?? 0) > 0) return true;

  return [row.kda, row.kills, row.deaths, row.assists, row.csPerMin, row.kpPct, row.dpm].some(
    hasStatValue,
  );
}

function isBenched(row: IPlayerSeasonStats): boolean {
  return row.benched === true;
}

function isSubstitute(row: IPlayerSeasonStats): boolean {
  return !isBenched(row) && (row.playerType ?? "").toUpperCase() === "SUBSTITUTE";
}

function isMainRoster(row: IPlayerSeasonStats): boolean {
  return !isBenched(row) && !isSubstitute(row);
}

function compareNullableNumbers(a: number | undefined, b: number | undefined): number {
  const aMissing = a == null || Number.isNaN(a);
  const bMissing = b == null || Number.isNaN(b);
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  return a - b;
}

function compareStrings(a: string | undefined, b: string | undefined): number {
  return (a ?? "").localeCompare(b ?? "", "fi", { sensitivity: "base" });
}

export function filterSeasonStats(
  rows: IPlayerSeasonStats[],
  filters: SeasonStatsFilters,
): IPlayerSeasonStats[] {
  const teamId = filters.teamId ?? null;
  const role = filters.role ?? null;
  const roster = filters.roster ?? "all";

  return rows.filter((row) => {
    if (!hasSeasonStats(row)) return false;
    if (teamId && row.teamId !== teamId) return false;
    if (role && normalizeSeasonStatsRole(row.role) !== role) return false;
    if (roster === "main" && !isMainRoster(row)) return false;
    if (roster === "substitute" && !isSubstitute(row)) return false;
    if (roster === "benched" && !isBenched(row)) return false;
    return true;
  });
}

export function sortSeasonStats(
  rows: IPlayerSeasonStats[],
  sortKey: SeasonStatsSortKey,
  direction: SortDirection,
): IPlayerSeasonStats[] {
  const multiplier = direction === "desc" ? -1 : 1;

  return [...rows].sort((a, b) => {
    let result = 0;

    switch (sortKey) {
      case "playerName":
        result = compareStrings(a.playerName, b.playerName);
        break;
      case "teamId":
        result = compareStrings(a.teamId, b.teamId);
        break;
      case "role":
        result = compareStrings(
          normalizeSeasonStatsRole(a.role) ?? a.role,
          normalizeSeasonStatsRole(b.role) ?? b.role,
        );
        break;
      case "kda":
        result = compareNullableNumbers(a.kda, b.kda);
        break;
      case "kills":
        result = compareNullableNumbers(a.kills, b.kills);
        break;
      case "deaths":
        result = compareNullableNumbers(a.deaths, b.deaths);
        break;
      case "assists":
        result = compareNullableNumbers(a.assists, b.assists);
        break;
      case "csPerMin":
        result = compareNullableNumbers(a.csPerMin, b.csPerMin);
        break;
      case "kpPct":
        result = compareNullableNumbers(a.kpPct, b.kpPct);
        break;
      case "dpm":
        result = compareNullableNumbers(a.dpm, b.dpm);
        break;
      case "doubleKills":
        result = compareCountNumbers(a.doubleKills, b.doubleKills);
        break;
      case "tripleKills":
        result = compareCountNumbers(a.tripleKills, b.tripleKills);
        break;
      case "quadraKills":
        result = compareCountNumbers(a.quadraKills, b.quadraKills);
        break;
      case "pentaKills":
        result = compareCountNumbers(a.pentaKills, b.pentaKills);
        break;
      case "gamesPlayed":
        result = compareCountNumbers(a.gamesPlayed, b.gamesPlayed);
        break;
      case "champsPlayed":
        result = compareCountNumbers(a.champsPlayed, b.champsPlayed);
        break;
      default:
        result = 0;
    }

    if (result === 0 && isMultikillSortKey(sortKey)) {
      result = compareMultikillTiebreaker(a, b, sortKey);
    }

    if (result === 0) {
      result = compareStrings(a.playerName, b.playerName);
    }

    return result * multiplier;
  });
}

export function formatStat(value: number | undefined, digits = 1): string {
  if (value == null || Number.isNaN(value)) return "—";
  return value.toFixed(digits);
}

export function formatCountStat(value: number | undefined): string {
  return String(normalizeCount(value));
}

function normalizeCount(value: number | undefined): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value;
}

function compareCountNumbers(a: number | undefined, b: number | undefined): number {
  return normalizeCount(a) - normalizeCount(b);
}

function getMultikillCount(row: IPlayerSeasonStats, key: SeasonStatsSortKey): number {
  switch (key) {
    case "doubleKills":
      return normalizeCount(row.doubleKills);
    case "tripleKills":
      return normalizeCount(row.tripleKills);
    case "quadraKills":
      return normalizeCount(row.quadraKills);
    case "pentaKills":
      return normalizeCount(row.pentaKills);
    default:
      return 0;
  }
}

function compareMultikillTiebreaker(
  a: IPlayerSeasonStats,
  b: IPlayerSeasonStats,
  primaryKey: SeasonStatsSortKey,
): number {
  for (const key of MULTIKILL_TIER_ORDER) {
    if (key === primaryKey) continue;

    const diff = compareCountNumbers(getMultikillCount(a, key), getMultikillCount(b, key));
    if (diff !== 0) return diff;
  }

  return 0;
}

export function hasSeasonMultikillStats(rows: IPlayerSeasonStats[]): boolean {
  return rows.some(
    (row) =>
      (row.doubleKills ?? 0) > 0 ||
      (row.tripleKills ?? 0) > 0 ||
      (row.quadraKills ?? 0) > 0 ||
      (row.pentaKills ?? 0) > 0,
  );
}
